import { useAuth0 } from "@/Contexts";
import { useTerminalsState } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { ExportType, downloadExportFile } from "@/api/export";
import { useConfig } from "@/api/hooks/useConfig";
import { CustomerList } from "@/api/hooks/useCustomers/useCustomers";
import { useSelectedSite } from "@/api/hooks/useSelectedSite";
import { ExportButton } from "@/components/ExportButton";
import { Toggle } from "@/components/Toggle";
import { Card, CardComponentVariant } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import { OPTION_ALL } from "@/components/pageCards/filterSort/constants";
import { useTrans } from "@/translations";

import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { SortingState } from "@tanstack/react-table";

interface ExportDataCardProps {
  selectedCustomers: CustomerList;
  areAllSelected?: boolean; // Export all customers even beyond the current page if true
  filterState?: ActiveOptions<any>;
  sortingState?: SortingState;
  nameFilter?: string;
}

export const ExportDataCard: FC<ExportDataCardProps> = ({
  selectedCustomers,
  areAllSelected,
  filterState = {},
  sortingState,
  nameFilter,
}) => {
  const { t, language } = useTrans();
  const { isMWM } = useConfig();
  const { currentTerminal } = useTerminalsState();
  const [isExporting, setIsExporting] = useState<undefined | "csv" | "excel">(
    undefined,
  );
  const { setGlobalAlert } = useGlobalAlert();
  const { selectedSite } = useSelectedSite();

  const selectedIds = useMemo(
    () => selectedCustomers.map((customer) => customer.id),
    [selectedCustomers],
  );
  const [includeAccessTokens, setIncludeAccessTokens] = useState(false);
  const [includeContacts, setIncludeContacts] = useState(false);
  const [includeStations, setIncludeStations] = useState(false);

  const { getTokenSilently } = useAuth0()!;

  const exportFile = useCallback(async () => {
    try {
      const multipleFiles =
        includeStations || includeContacts || includeAccessTokens;

      let type: ExportType = "csv";
      if (multipleFiles && isExporting === "csv") {
        type = "zip";
      } else if (isExporting === "excel") {
        type = "excel";
      }

      const types = filterState?.type?.filter((t) => t.value !== OPTION_ALL);
      const categories = filterState?.category?.filter(
        (t) => t.value !== OPTION_ALL,
      );
      const points = filterState?.accessParentId?.filter(
        (t) => t.value !== OPTION_ALL,
      );

      await downloadExportFile({
        type,
        entity: "customers",
        queryParams: {
          includeStations,
          includeContacts,
          includeAccessTokens,
          customerIds: areAllSelected ? undefined : selectedIds,
          filterSort: {
            nameRegex: nameFilter,
            sortDirection: (sortingState?.[0]?.desc ?? false) ? "desc" : "asc",
            sortFields: (sortingState?.map((ss) => ss.id) as (
              "name" | "createdAt" | "updatedAt"
            )[]) ?? ["name"],
            types: types?.length ? types.map((el) => el.value) : undefined,
            categoryIds: categories?.length
              ? categories.map((el) => el.value)
              : undefined,
            pointIds: points?.length ? points.map((el) => el.value) : undefined,
          },
        },
        terminalId: isMWM ? undefined : currentTerminal?.id,
        language,
        t,
        getTokenSilently,
        selectedSite,
      });
    } catch (error) {
      console.error("There was an error downloading the file!", error);
      setGlobalAlert({
        message: t("alerts:exportFailed"),
        type: "error",
      });
    }
    setIsExporting(undefined);
  }, [
    isExporting,
    includeStations,
    includeContacts,
    includeAccessTokens,
    currentTerminal?.id,
    getTokenSilently,
    setGlobalAlert,
    areAllSelected,
    selectedIds,
    isMWM,
    language,
    t,
    nameFilter,
    sortingState,
    filterState,
    selectedSite,
  ]);

  useEffect(() => {
    if (isExporting) {
      exportFile();
    }
  }, [isExporting, exportFile]);

  return (
    <Card
      title={"drawerLabels:exportData"}
      variant={CardComponentVariant.DRAWER}
    >
      <p className="font-dm-sans mb-4 text-sm text-black">
        {t("hints:exportCustomerDataDescription")}
      </p>
      <ul>
        {isMWM ? (
          <>
            <li className="mb-2">
              <Toggle
                label={t("drawerLabels:includeStations")}
                checked={includeStations}
                onChange={() => setIncludeStations(!includeStations)}
              />
            </li>
            <li className="mb-2">
              <Toggle
                label={t("drawerLabels:includeAccessTokens")}
                checked={includeAccessTokens}
                onChange={() => setIncludeAccessTokens(!includeAccessTokens)}
              />
            </li>
          </>
        ) : (
          <>
            <li className="mb-2">
              <Toggle
                label={t("drawerLabels:includeContacts")}
                checked={includeContacts}
                onChange={() => setIncludeContacts(!includeContacts)}
              />
            </li>
            <li className="mb-2">
              <Toggle
                label={t("drawerLabels:includeAccessTokens")}
                checked={includeAccessTokens}
                onChange={() => setIncludeAccessTokens(!includeAccessTokens)}
              />
            </li>
          </>
        )}
      </ul>
      <div className="right-8 z-9 flex justify-end">
        <ExportButton
          isLoading={!!isExporting}
          nrSelected={selectedCustomers.length}
          areAllSelected={areAllSelected}
          exportCsvFunction={() => setIsExporting("csv")}
          exportExcelFunction={() => setIsExporting("excel")}
        />
      </div>
    </Card>
  );
};
